import * as THREE from 'three';
import BaseModel3d from '../BaseModel3d';
import { globalStores } from '@/editor/stores';
import { calPlaneOutline2 } from '../../plane/utils';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { pixelRatio3d, thresholdValue } from '@/editor/math/const';
import { IBaseDataEdgeFace, IFaceData } from '@/editor/data/face/face';
import { EEntityType } from '@/editor/types';
import { EContainerType } from '@/editor/types/entity';
import { createMaterial } from '@/editor/data/material/utils';
import { normalizeAngle } from '@/editor/math/utils';
import { booleanPolygonUnion } from '@/editor/model/polygon';

export class EdgeFaceModel extends BaseModel3d {
  private _planeMesh?: THREE.Mesh;

  constructor(options: { id: string }) {
    super({ id: options.id });

    this.model.userData.type = EEntityType.Face;
    this.model.userData.containerType = EContainerType.entity;
    this.model.userData.entityId = this._id;

    globalStores.editorStateStore.sceneInfo.scene3d.intersectableGroup.add(this.model);
  }

  createModel(faceData: IFaceData) {
    if (!this.canUpdate) return;

    const { start, end } = faceData.baseData as IBaseDataEdgeFace;

    // 创建平面
    this._planeMesh = this.createPlane(faceData);

    // 旋转平移
    const angle = normalizeAngle(Point2dUtils.getAngle(start, end) + Math.PI);
    this._planeMesh.rotateZ(angle);
    this._planeMesh.rotateX(Math.PI / 2);
    const lineCenter = Point2dUtils.getCenter(start, end);
    this._planeMesh.position.set(lineCenter.x / pixelRatio3d, lineCenter.y / pixelRatio3d, 0);

    // 添加到容器
    this.model.add(this._planeMesh);

    this.updateWithoutDestroy(faceData);
  }

  private createPlane(faceData: IFaceData) {
    const { start, end, height, offGround } = faceData.baseData as IBaseDataEdgeFace;
    const lineLength = Point2dUtils.distanceTo(start, end);
    const outline = calPlaneOutline2(lineLength, height, offGround);

    const shape = new THREE.Shape();
    outline.forEach((point, index) => {
      if (index === 0) {
        shape.moveTo(point.x / pixelRatio3d, point.y / pixelRatio3d);
      } else {
        shape.lineTo(point.x / pixelRatio3d, point.y / pixelRatio3d);
      }
    });

    if (faceData.holes.length > 0) {
      const holes = faceData.holes.map((hole) => hole.map((p) => ({ x: p.ratioX * lineLength, y: p.y })));
      booleanPolygonUnion(holes, thresholdValue).forEach((hole) => {
        const holePath = new THREE.Path();
        hole.forEach((point, index) => {
          // x轴取反，因为start-end方向和x轴方向相反
          if (index === 0) {
            holePath.moveTo(-(point.x - lineLength / 2) / pixelRatio3d, point.y / pixelRatio3d);
          } else {
            holePath.lineTo(-(point.x - lineLength / 2) / pixelRatio3d, point.y / pixelRatio3d);
          }
        });
        holePath.closePath();
        shape.holes.push(holePath);
      });
    }

    const geometry = new THREE.ShapeGeometry(shape);
    const material = createMaterial(faceData.materialInfo.id);
    const plane = new THREE.Mesh(geometry, material);

    return plane;
  }

  updateWithoutDestroy(faceData: IFaceData) {
    if (!this.canUpdate) return;

    if (this._planeMesh) {
      const material = createMaterial(faceData.materialInfo.id);
      this._planeMesh.material = material;
    }
  }
}
